import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { merge, Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatSelectChange } from '@angular/material/select';
import { MatSelect } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../../confirm-dialog/confirm-dialog.component';

import {
  RULE_VALUE_CHIP_PLACEHOLDER,
  RULE_VALUE_MAX_COUNT,
} from '../../../rule-config-component/models/rule-engine';
import {
  getRuleNameValidationMessage,
  RuleEngineService,
} from '../../../services/rule-engine.service';
import type { RoutingPipelineBlock } from '../../models/routing-ruleconfig1.models';
import type { RuleExpression } from '../../../rule-config-component/models/rule-engine';
import type { XmlFieldOption } from '../../models/field-option.model';
import { RoutingRuleconfig1Hl7RulesService } from '../../services/routing-ruleconfig1-hl7-rules.service';
import { XmlFieldCatalogService } from '../../services/xml-field-catalog.service';
import { routingExpressionPrep } from '../../services/routing-ruleconfig1-expression-prep.util';
import {
  handleMatAutocompleteArrowKeys,
  handleSegmentedArrowNav,
  RoutingAutocompletePanelHelper,
} from '../../utils/routing-conversion-type.util';
import { RoutingMatSelectOverlayHelper } from '../../utils/routing-mat-select-overlay.util';

@Component({
  selector: 'app-routing-rules-right-panel',
  templateUrl: './routing-rules-right-panel.component.html',
  styleUrls: ['./routing-rules-right-panel.component.scss'],
  host: { class: 'routing-pipeline-right' },
})
export class RoutingRulesRightPanelComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('operatorSelectRef') operatorSelectRef!: MatSelect;
  private readonly matSelectOverlay = new RoutingMatSelectOverlayHelper();

  readonly ruleValueChipPlaceholder = RULE_VALUE_CHIP_PLACEHOLDER;
  readonly ruleValueMaxCount = RULE_VALUE_MAX_COUNT;

  ruleValueChipInput = new FormControl('');
  ruleValueChips: string[] = [];
  private syncingRuleValueChips = false;
  private ruleFormChipSubscription: Subscription | null = null;

  @Input() pipelineBlocks: RoutingPipelineBlock[] = [];
  @Input() selectedBlock: RoutingPipelineBlock | null = null;
  @Input() selectedPipelineInBlocks = true;
  @Input() ruleForm!: FormGroup;
  @Input() isHl7 = false;
  @Input() isEditingRule = false;
  @Input() editingBlockId: string | null = null;
  @Input() editingRuleIndex: number | null = null;
  @Input() showBuildPath = false;
  @Input() fieldDisplay: (field: string) => string = (f) => f;

  @Output() fieldFocus = new EventEmitter<void>();
  @Output() fieldOptionSelected = new EventEmitter<string>();
  @Output() openBuildPath = new EventEmitter<void>();
  @Output() saveOrUpdate = new EventEmitter<void>();
  @Output() clearForm = new EventEmitter<void>();
  @Output() editRule = new EventEmitter<{ blockId: string; index: number }>();
  @Output() deleteRule = new EventEmitter<{ blockId: string; index: number }>();
  @Output() buildExpression = new EventEmitter<RoutingPipelineBlock>();
  @Output() deletePipelineBlock = new EventEmitter<RoutingPipelineBlock>();
  /**
   * ADDED: Emitted when Operator = CUSTOM so parent can open CustomRulesComponent
   * without changing the existing IN / NOT IN add-form flow.
   */
  @Output() openCustomRules = new EventEmitter<void>();

  filteredFieldOptions$!: Observable<XmlFieldOption[]>;

  private readonly autocompletePanelHelper = new RoutingAutocompletePanelHelper();

  @ViewChild('routingHl7SegmentInput', { read: MatAutocompleteTrigger })
  private hl7SegmentAutocompleteTrigger?: MatAutocompleteTrigger;
  @ViewChild('routingHl7FieldShell') private routingHl7FieldShell?: ElementRef<HTMLElement>;

  @ViewChild('routingFieldInput', { read: MatAutocompleteTrigger })
  private xmlFieldAutocompleteTrigger?: MatAutocompleteTrigger;

  /**
   * Injects HL7 rules, XML field catalog, and toast services for the rules panel.
   * @example isHl7 route → hl7Rules drives segment autocomplete; XML → catalog.search filters field options
   */
  constructor(
    readonly hl7Rules: RoutingRuleconfig1Hl7RulesService,
    private readonly catalog: XmlFieldCatalogService,
    private readonly toastr: ToastrService,
    readonly ruleEngineService: RuleEngineService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog
  ) {}

  /**
   * False when rule name (or number) appears in this pipeline block's Rule Expression.
   */
  canDeleteRule(rule: RuleExpression, block: RoutingPipelineBlock): boolean {
    return this.ruleEngineService.canDeleteRule(
      rule,
      (block.ruleSatisfaction ?? '').toString()
    );
  }

  isDeleteRuleDisabled(
    rule: RuleExpression,
    block: RoutingPipelineBlock,
    index: number
  ): boolean {
    if (!this.canDeleteRule(rule, block)) {
      return true;
    }
    return (
      this.isEditingRule &&
      this.editingBlockId === block.id &&
      this.editingRuleIndex === index
    );
  }

  async onDeletePipelineBlockClick(block: RoutingPipelineBlock): Promise<void> {
    const label = (block.label ?? '').toString().trim() || 'this pipeline';
    const message = `Are you sure you want to delete pipeline "${label}"? All rules in this pipeline block will be removed.`;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '500px',
      data: new ConfirmDialogModel('Confirm Action', message, 'confirm', ''),
      disableClose: true,
    });
    const confirmed = await dialogRef.afterClosed().toPromise();
    if (confirmed) {
      this.deletePipelineBlock.emit(block);
    }
  }

  async onDeleteRuleClick(
    block: RoutingPipelineBlock,
    index: number,
    rule: RuleExpression
  ): Promise<void> {
    if (this.isDeleteRuleDisabled(rule, block, index)) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '500px',
      data: new ConfirmDialogModel(
        'Confirm Action',
        'Are you sure you want to delete this Rule?',
        'confirm',
        ''
      ),
      disableClose: true,
    });
    const confirmed = await dialogRef.afterClosed().toPromise();
    if (confirmed) {
      this.deleteRule.emit({ blockId: block.id, index });
    }
  }

  /**
   * Rule Expression bar — same labels as Build Expression.
   * @example with rulenames: "(test AND testobr)"
   * @example legacy: "(Rule1 AND Rule2)"
   */
  getRuleSatisfactionDisplay(block: RoutingPipelineBlock): string {
    const ruleCount = block.rules?.length ?? 0;
    if (ruleCount === 0) {
      return '';
    }
    return (
      routingExpressionPrep.resolveFinalExpressionForBlock(block) ||
      (block.ruleSatisfaction ?? '').toString().trim() ||
      '(none)'
    );
  }

  /**
   * Builds a tooltip title showing display label and persisted path for a rule field cell.
   * @example field 'PID.5.1', display 'Patient Name' → 'Patient Name\nPID.5.1'
   */
  // ruleFieldCellTitle(rule: { field?: string }): string {
  //   const persisted = (rule?.field ?? '').toString().trim();
  //   const display = this.fieldDisplay(persisted);
  //   if (!persisted || display === persisted) {
  //     return display || persisted;
  //   }
  //   return `${display}\n${persisted}`;
  // }

  /**
   * Copies rule cell text to the clipboard and shows a success toast.
   * @example copyRuleCellText('Case/AccessionNumber', 'Field') → clipboard + toast 'Field copied'
   */
  copyRuleCellText(text: string, label: string): void {
    const value = (text ?? '').toString().trim();
    if (!value) {
      return;
    }
    const done = () => {
      this.toastr.success(`${label} copied`, '', {
        timeOut: 2000,
        positionClass: 'toast-bottom-right',
      });
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(done).catch(() => this.fallbackCopyText(value, done));
    } else {
      this.fallbackCopyText(value, done);
    }
  }

  /**
   * Fallback clipboard copy using a temporary textarea when navigator.clipboard is unavailable.
   * @example fallbackCopyText('OBR.4.1', onSuccess) → document.execCommand('copy') then toast
   */
  private fallbackCopyText(text: string, onSuccess: () => void): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      onSuccess();
    } catch {
      this.toastr.error('Could not copy to clipboard', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
    }
    document.body.removeChild(ta);
  }

  /**
   * Loads the XML field catalog and binds autocomplete filtering on init.
   * @example XML route → catalog.ensureLoaded(); filteredFieldOptions$ reacts to field control typing 'Accession'
   */
  ngOnInit(): void {
    if (!this.isHl7) {
      this.catalog.ensureLoaded();
    }
    this.bindFieldOptions();
    this.bindRuleValueChipSync();
    this.loadRuleValueChipsFromForm();
  }

  /**
   * Rebinds field autocomplete when inputs change (e.g. switching HL7 ↔ XML).
   * @example isHl7 toggles true → bindFieldOptions returns early; false → rebinds catalog.search
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.bindFieldOptions();
    if (changes['ruleForm']) {
      this.bindRuleValueChipSync();
    }
    if (
      changes['isEditingRule'] ||
      changes['editingRuleIndex'] ||
      changes['editingBlockId'] ||
      changes['ruleForm']
    ) {
      this.scheduleRuleValueChipsReload();
    }
  }

  get useRuleValueChips(): boolean {
    return this.ruleEngineService.isMultiValueOperator(
      this.ruleForm?.get('operator')?.value
    );
  }

  private getRuleNameInputValue(): string {
    const control = this.ruleForm?.get('rulename');
    if (!control) {
      return '';
    }
    const raw = control.disabled ? control.getRawValue() : control.value;
    return (raw ?? '').toString().trim();
  }

  onRuleNameBlur(): void {
    const control = this.ruleForm?.get('rulename');
    control?.markAsTouched();
    this.showRuleNameValidationToastIfNeeded();
  }

  private showRuleNameValidationToastIfNeeded(): boolean {
    const message = getRuleNameValidationMessage(this.getRuleNameInputValue());
    if (!message) {
      return false;
    }
    this.toastr.warning(message, '', {
      timeOut: 4000,
      positionClass: 'toast-bottom-right',
    });
    return true;
  }

  /** Match Rule Config — allow Add when chips or pending chip input exist. */
  get isSaveDisabled(): boolean {
    if (!this.ruleForm) {
      return true;
    }
    const rulename = this.getRuleNameInputValue();
    const field = (this.ruleForm.get('field')?.value ?? '').toString().trim();
    const operator = (this.ruleForm.get('operator')?.value ?? '').toString().trim();
    if (!rulename || !field || !operator) {
      return true;
    }
    if (this.useRuleValueChips) {
      const pending = (this.ruleValueChipInput.value ?? '').toString().trim();
      return this.ruleValueChips.length === 0 && !pending;
    }
    const value = (this.ruleForm.get('value')?.value ?? '').toString().trim();
    return !value;
  }

  /** Same as create-rules clearTheValues — parent resets form; panel clears chip UI. */
  onClearFormClick(): void {
    this.clearForm.emit();
    this.clearRuleValueChips(false);
    this.cdr.markForCheck();
  }

  getRuleValueDisplay(value: string | undefined): ReturnType<
    RuleEngineService['formatRuleValueDisplay']
  > {
    return this.ruleEngineService.formatRuleValueDisplay(value);
  }

  onOperatorSelectionChange(event: MatSelectChange): void {
    // ADDED: CUSTOM is handled by parent (Custom Rules dialog), same pattern as create-rules.
    const selected = (event?.value ?? '').toString().trim().toLowerCase();
    if (selected === 'custom') {
      this.openCustomRules.emit();
      return;
    }

    if (this.ruleEngineService.isMultiValueOperator(event.value)) {
      const existing = this.ruleEngineService.normalizeRuleValue(
        this.ruleForm?.get('value')?.value ?? ''
      );
      this.loadRuleValueChipsFromValue(existing);
    } else {
      this.clearRuleValueChips(false);
    }
  }

  /** Flush typed text into chips before save — first value is always stored as a chip. */
  commitPendingRuleValueChipInput(): void {
    if (!this.useRuleValueChips) {
      return;
    }
    const raw = (this.ruleValueChipInput.value ?? '').toString().trim();
    if (!raw) {
      return;
    }
    this.onAddRuleValueFromInput();
  }

  onSaveOrUpdateClick(): void {
    this.commitPendingRuleValueChipInput();
    this.ruleForm?.get('rulename')?.markAsTouched();
    if (this.showRuleNameValidationToastIfNeeded()) {
      return;
    }
    if (this.isSaveDisabled) {
      return;
    }
    if (this.useRuleValueChips && this.ruleValueChips.length === 0) {
      const stored = (this.ruleForm?.get('value')?.value ?? '').toString().trim();
      if (stored) {
        this.applyRuleValueChipsFromStored(stored, true);
      }
    }
    this.saveOrUpdate.emit();
  }

  onAddRuleValueFromInput(): void {
    const raw = (this.ruleValueChipInput.value ?? '').toString().trim();
    if (!raw) {
      return;
    }
    const result = this.ruleEngineService.appendRuleValuesFromInput(
      this.ruleValueChips,
      raw,
      this.ruleValueMaxCount
    );
    if (result.limitReached) {
      this.toastr.warning(
        `Maximum ${this.ruleValueMaxCount} values allowed`,
        '',
        { timeOut: 4000, positionClass: 'toast-bottom-right' }
      );
    }
    this.ruleValueChips = [...result.values];
    this.ruleValueChipInput.reset();
    this.syncRuleValueChipsToForm();
    this.cdr.markForCheck();
  }

  removeRuleValueChip(chip: string): void {
    const index = this.ruleValueChips.indexOf(chip);
    if (index >= 0) {
      this.ruleValueChips.splice(index, 1);
      this.syncRuleValueChipsToForm();
    }
  }

  private bindRuleValueChipSync(): void {
    this.ruleFormChipSubscription?.unsubscribe();
    const valueControl = this.ruleForm?.get('value');
    const operatorControl = this.ruleForm?.get('operator');
    if (!valueControl || !operatorControl) {
      return;
    }
    this.ruleFormChipSubscription = merge(
      valueControl.valueChanges.pipe(startWith(valueControl.value)),
      operatorControl.valueChanges.pipe(startWith(operatorControl.value))
    ).subscribe(() => {
      if (this.syncingRuleValueChips) {
        return;
      }
      this.loadRuleValueChipsFromForm();
    });
  }

  /** Reload chips after parent patchValue (startEditRule / clear). */
  private scheduleRuleValueChipsReload(): void {
    this.loadRuleValueChipsFromForm();
    setTimeout(() => this.loadRuleValueChipsFromForm(), 0);
  }

  private loadRuleValueChipsFromForm(): void {
    if (!this.useRuleValueChips) {
      this.clearRuleValueChips(false);
      return;
    }
    const stored = (this.ruleForm?.get('value')?.value ?? '').toString();
    this.applyRuleValueChipsFromStored(stored, false);
  }

  private applyRuleValueChipsFromStored(
    value: unknown,
    syncForm: boolean
  ): void {
    this.ruleValueChips = [...this.ruleEngineService.parseRuleValues(value)];
    this.ruleValueChipInput.reset();
    if (syncForm) {
      this.syncRuleValueChipsToForm();
    }
    this.cdr.markForCheck();
  }

  private loadRuleValueChipsFromValue(value: string | undefined): void {
    this.applyRuleValueChipsFromStored(value, true);
  }

  private syncRuleValueChipsToForm(): void {
    const joined = this.ruleEngineService.joinRuleValues(this.ruleValueChips);
    this.syncingRuleValueChips = true;
    this.ruleForm?.patchValue({ value: joined }, { emitEvent: false });
    this.syncingRuleValueChips = false;
  }

  private clearRuleValueChips(syncForm = true): void {
    this.ruleValueChips = [];
    this.ruleValueChipInput.reset();
    if (syncForm) {
      this.syncRuleValueChipsToForm();
    }
  }

  /**
   * Wires ruleForm.field valueChanges to XML catalog search for autocomplete options.
   * @example user types 'Case' → filteredFieldOptions$ emits XmlFieldOption[] matching Case/* paths
   */
  private bindFieldOptions(): void {
    if (this.isHl7) {
      return;
    }
    const fieldControl = this.ruleForm?.get('field');
    if (fieldControl) {
      this.filteredFieldOptions$ = fieldControl.valueChanges.pipe(
        startWith(fieldControl.value || ''),
        map((value) => this.catalog.search((value ?? '').toString()))
      );
    }
  }

  /**
   * Removes wheel scroll listeners when the component is destroyed.
   * @example ngOnDestroy → detachWheelScroll() clears document wheel handler
   */
  ngOnDestroy(): void {
    this.autocompletePanelHelper.destroy();
    this.ruleFormChipSubscription?.unsubscribe();
    this.matSelectOverlay.destroy();
  }

  onOperatorSelectOpenedChange(opened: boolean): void {
    this.matSelectOverlay.onOpenedChange(opened, this.operatorSelectRef);
  }

  /**
   * Emits fieldFocus and enables wheel scrolling inside the XML field autocomplete panel.
   * @example autocomplete opens → fieldFocus.emit(); wheel scrolls .routing-field-autocomplete-panel
   */
  onFieldAutocompleteOpened(): void {
    this.fieldFocus.emit();
    this.autocompletePanelHelper.attach('.routing-field-autocomplete-panel');
  }

  onFieldAutocompleteClosed(): void {
    this.autocompletePanelHelper.detach();
  }

  onHl7SegmentAutocompleteOpened(): void {
    this.hl7Rules.onHl7SegmentInputFocus();
    this.autocompletePanelHelper.attach('.routing-hl7-segment-autocomplete-panel');
  }

  onHl7SegmentAutocompleteClosed(): void {
    this.autocompletePanelHelper.detach();
  }

  /** ArrowLeft/Right moves between segment and index inputs; ArrowDown opens segment autocomplete. */
  onHl7PickerArrowNav(
    event: KeyboardEvent,
    field: 'segment' | 'fieldIndex' | 'componentIndex' | 'subComponent'
  ): void {
    const shell = this.routingHl7FieldShell?.nativeElement;
    if (shell && handleSegmentedArrowNav(event, shell)) {
      return;
    }
    if (field === 'segment') {
      handleMatAutocompleteArrowKeys(event, this.hl7SegmentAutocompleteTrigger);
    }
  }

  /** ArrowDown opens XML field autocomplete when the panel is closed. */
  onXmlFieldArrowNav(event: KeyboardEvent): void {
    handleMatAutocompleteArrowKeys(event, this.xmlFieldAutocompleteTrigger);
  }
}
